import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import {
  GraduationCap,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ArrowLeft
} from 'lucide-react'

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'La contraseña es requerida')
})

type LoginForm = z.infer<typeof loginSchema>

export default function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { signIn, isAuthenticated, profile } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loginSuccess, setLoginSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema)
  })

  // Navegar cuando el perfil esté cargado después de un login exitoso
  useEffect(() => {
    if (loginSuccess && isAuthenticated && profile) {
      // Redirigir según el rol
      if (profile.role === 'admin') {
        navigate('/admin/dashboard', { replace: true })
      } else if (profile.role === 'teacher') {
        navigate('/teacher/dashboard', { replace: true })
      } else {
        navigate('/dashboard', { replace: true })
      }
    }
  }, [loginSuccess, isAuthenticated, profile, navigate])

  // Timeout si el perfil no se carga después de 8 segundos
  useEffect(() => {
    if (loginSuccess && !profile) {
      const timeout = setTimeout(() => {
        toast.error('Error al cargar el perfil. Intenta cerrar sesión y volver a iniciar.')
        setLoginSuccess(false)
        setIsLoading(false)
      }, 8000)
      return () => clearTimeout(timeout)
    }
  }, [loginSuccess, profile])

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      const { error } = await signIn(data.email, data.password)

      if (error) {
        toast.error(t('auth.invalidCredentials'))
        setIsLoading(false)
      } else {
        toast.success(t('auth.loginSuccess'))
        setLoginSuccess(true)
        // No navegamos aquí, dejamos que el useEffect lo haga cuando el perfil esté listo
      }
    } catch {
      toast.error(t('errors.generic'))
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Panel izquierdo - Formulario */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md mx-auto">
          {/* Volver */}
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <GraduationCap className="w-10 h-10 text-primary" />
            <span className="text-2xl font-bold">
              Nomad<span className="text-[#EF4135]">Lexis</span>
            </span>
          </Link>

          {/* Título */}
          <h1 className="text-3xl font-bold mb-2">
            ¡Bienvenido de vuelta!
          </h1>
          <p className="text-muted-foreground mb-8">
            Inicia sesión para continuar tu aprendizaje
          </p>

          {/* Formulario */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="email"
                  {...register('email')}
                  className={cn(
                    'w-full pl-10 pr-4 py-3 rounded-lg border bg-background',
                    'focus:outline-none focus:ring-2 focus:ring-primary',
                    errors.email && 'border-red-500'
                  )}
                  placeholder="email@exemple.com"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  className={cn(
                    'w-full pl-10 pr-12 py-3 rounded-lg border bg-background',
                    'focus:outline-none focus:ring-2 focus:ring-primary',
                    errors.password && 'border-red-500'
                  )}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Forgot password */}
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading && <Loader2 className="w-5 h-5 animate-spin" />}
              {t('auth.signIn')}
            </button>
          </form>

          {/* Register link */}
          <p className="text-center mt-6 text-muted-foreground">
            {t('auth.noAccount')}{' '}
            <Link to="/planes" className="text-primary font-medium hover:underline">
              {t('auth.signUp')}
            </Link>
          </p>
        </div>
      </div>

      {/* Panel derecho - Imagen decorativa */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#0055A4] via-white to-[#EF4135] items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center gap-2 mb-8">
            <div className="w-12 h-16 bg-[#0055A4] rounded-l shadow-lg" />
            <div className="w-12 h-16 bg-white shadow-lg" />
            <div className="w-12 h-16 bg-[#EF4135] rounded-r shadow-lg" />
          </div>
          <h2 className="text-3xl font-bold text-[#0055A4] mb-2">
            Bienvenue!
          </h2>
          <p className="text-lg text-gray-700">
            Tu viaje en francés comienza aquí
          </p>
        </div>
      </div>
    </div>
  )
}
